import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SubHeader from '../../layout/components/SubHeader';
import WebBookingsNav from './subparts/WebBookingsNav';
import BookingRequests from './BookingRequests';
import bookingService from '../../../services/booking.service';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import { buildBookingRequestParams, DEFAULT_WEB_BOOKING_SEARCH, WEB_BOOKING_TABS } from './subparts/webBookingsQuery';
import { WEB_BOOKINGS_PAGE_THEME } from './subparts/webBookingsTheme';
import './WebBookings.css';

function Bookings() {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);

    const [activeTab, setActiveTab] = useState('new');
    const [loadedTabs, setLoadedTabs] = useState(() => new Set(['new']));
    const [tabCounts, setTabCounts] = useState({});
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [refreshToken, setRefreshToken] = useState(0);

    const fetchNewRequestCount = useCallback(async () => {
        if (!clinicId) {
            return;
        }

        try {
            const res = await bookingService.getBookingRequests(
                clinicId,
                0,
                1,
                buildBookingRequestParams(0, DEFAULT_WEB_BOOKING_SEARCH)
            );
            setTabCounts((prev) => ({ ...prev, new: res.data?.totalElements ?? 0 }));
        } catch (error) {
            console.error('Failed to load new request count', error);
        }
    }, [clinicId]);

    useEffect(() => {
        fetchNewRequestCount();
    }, [fetchNewRequestCount, refreshToken]);

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        setLoadedTabs((prev) => new Set([...prev, tabId]));
    };

    const handleCountUpdate = useCallback((tabId, count) => {
        setTabCounts((prev) => {
            if (prev[tabId] === count) {
                return prev;
            }

            return { ...prev, [tabId]: count };
        });
    }, []);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            setRefreshToken((token) => token + 1);
        } finally {
            setIsRefreshing(false);
        }
    };

    const description = useMemo(() => {
        const newCount = tabCounts.new ?? 0;

        if (newCount) {
            return `Clinic website booking requests · ${newCount} new request${newCount === 1 ? '' : 's'} awaiting review`;
        }

        return 'Review and manage appointment requests submitted through your clinic website';
    }, [tabCounts.new]);

    return (
        <div className="ecx-page web-bookings-page ecx-page--full-width">
            <SubHeader
                title="Web Bookings"
                description={description}
                icon="bi-calendar-check"
                iconTheme={WEB_BOOKINGS_PAGE_THEME}
                containerClass="w-100 px-4"
            >
                <button
                    type="button"
                    className="btn btn-light-primary btn-sm rounded-pill web-bookings-refresh-btn"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    title="Refresh booking requests"
                >
                    <i className={`bi bi-arrow-clockwise me-1 ${isRefreshing ? 'ecx-refresh-spin' : ''}`} />
                    Refresh
                </button>
            </SubHeader>

            <div className="ecx-page-body">
                <div className="ecx-page-card ecx-page-panel web-bookings-panel">
                    <div className="ecx-page-panel__header web-bookings-panel__header border-top-0">
                        <WebBookingsNav
                            tabs={WEB_BOOKING_TABS}
                            activeTab={activeTab}
                            onTabChange={handleTabChange}
                            counts={tabCounts}
                        />
                    </div>

                    <div className="ecx-page-panel__body web-bookings-panel__body">
                        {WEB_BOOKING_TABS.map((tab) =>
                            loadedTabs.has(tab.id) ? (
                                <div
                                    key={tab.id}
                                    className={
                                        activeTab === tab.id
                                            ? 'h-100 d-flex flex-column min-h-0'
                                            : 'd-none'
                                    }
                                >
                                    <BookingRequests
                                        bookingStatus={tab.status}
                                        tabId={tab.id}
                                        tabLabel={tab.label}
                                        refreshToken={refreshToken}
                                        onCountUpdate={tab.id === 'new' ? handleCountUpdate : undefined}
                                    />
                                </div>
                            ) : null
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Bookings;
